import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { lucideLock } from '@ng-icons/lucide';
import { HlmButtonDirective } from '@spartan-ng/helm/button';
import { HlmInputDirective } from '@spartan-ng/helm/input';
import { HlmSelectImports } from '@spartan-ng/helm/select';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { Analysis } from '@shared/models/analysis.model';

@Component({
  selector: 'app-analysis-detail-address-details',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgIconComponent,
    HlmButtonDirective,
    HlmInputDirective,
    ...HlmSelectImports,
    ...BrnSelectImports,
  ],
  providers: [
    provideIcons({ lucideLock }),
  ],
  templateUrl: './analysis-detail-address-details.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnalysisDetailAddressDetails {
  analysis = input.required<Analysis>();
  saveStatus = input<'idle' | 'saving' | 'saved' | 'error'>('idle');
  lastSavedAt = input<Date | null>(null);
  closeRequested = output<void>();

  detailsUpdated = output<{
    powerConsumption: number;
    electricityPrice: number;
    addressData: {
      roofType?: 'flat' | 'normal' | 'both' | null;
      isApartmentBuilding?: boolean | null;
      hasSolar?: boolean | null;
    };
  }>();

  editPower = signal(0);
  editPrice = signal(0);
  editRoofType = signal<string | null>(null);
  editBuildingType = signal<string | null>(null);
  editSolarStatus = signal<string | null>(null);

  address = computed(() => this.analysis().address);

  isDirty = computed(() => {
    const an = this.analysis();
    const addr = an.address;
    if (this.editPower() !== (an.powerConsumption ?? 0)) return true;
    if (this.editPrice() !== (an.electricityPrice ?? 0)) return true;
    if (this.editRoofType() !== (addr?.roofType ?? null)) return true;
    const currentBuildingType =
      addr?.isApartmentBuilding === true
        ? 'multi_family'
        : addr?.isApartmentBuilding === false
          ? 'single_family'
          : null;
    if (this.editBuildingType() !== currentBuildingType) return true;
    const currentSolarStatus =
      addr?.hasSolar === true ? 'yes' : addr?.hasSolar === false ? 'no' : null;
    if (this.editSolarStatus() !== currentSolarStatus) return true;
    return false;
  });

  lastSavedAtFormatted = computed(() => {
    const d = this.lastSavedAt();
    if (!d) return '';
    return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  });

  constructor() {
    effect(() => {
      const analysis = this.analysis();
      const addr = analysis.address;
      this.editPower.set(analysis.powerConsumption ?? 0);
      this.editPrice.set(analysis.electricityPrice ?? 0);
      if (addr) {
        this.editRoofType.set(addr.roofType ?? null);
        this.editBuildingType.set(
          addr.isApartmentBuilding === true
            ? 'multi_family'
            : addr.isApartmentBuilding === false
              ? 'single_family'
              : null,
        );
        this.editSolarStatus.set(
          addr.hasSolar === true ? 'yes' : addr.hasSolar === false ? 'no' : null,
        );
      }
    });
  }

  cancel(): void {
    this.resetValues();
    this.closeRequested.emit();
  }

  save(): void {
    if (this.editPower() < 0 || this.editPrice() < 0) return;
    if (this.isDirty()) this._emitSave();
    this.closeRequested.emit();
  }

  private _emitSave(): void {
    this.detailsUpdated.emit({
      powerConsumption: this.editPower(),
      electricityPrice: this.editPrice(),
      addressData: {
        roofType: this.editRoofType() as any,
        isApartmentBuilding:
          this.editBuildingType() === 'multi_family'
            ? true
            : this.editBuildingType() === 'single_family'
              ? false
              : null,
        hasSolar:
          this.editSolarStatus() === 'yes'
            ? true
            : this.editSolarStatus() === 'no'
              ? false
              : null,
      },
    });
  }

  private resetValues(): void {
    const analysis = this.analysis();
    const address = analysis.address;
    this.editPower.set(analysis.powerConsumption ?? 0);
    this.editPrice.set(analysis.electricityPrice ?? 0);
    this.editRoofType.set(address?.roofType ?? null);
    this.editBuildingType.set(
      address?.isApartmentBuilding === true
        ? 'multi_family'
        : address?.isApartmentBuilding === false
          ? 'single_family'
          : null,
    );
    this.editSolarStatus.set(
      address?.hasSolar === true ? 'yes' : address?.hasSolar === false ? 'no' : null,
    );
  }
}
